import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Loader2, Plus, ShieldAlert, Trash2, X, Phone, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { useOffline } from "@/context/OfflineContext";
import { isOfflineError } from "@/lib/offline";
import { BodyMap } from "@/components/worker/BodyMap";
import { orgNowLocal } from "@/lib/tz";

const TYPES = [
  ["behaviour", "Behaviour"], ["injury", "Injury"], ["self_harm", "Self Harm"],
  ["medication", "Medication"], ["vehicle", "Vehicle"], ["other", "Other"],
];
const SEVERITIES = ["Low", "Medium", "High", "Critical"];
const SEV_STYLE = {
  Low: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
  Medium: "text-amber-300 border-amber-500/30 bg-amber-500/10",
  High: "text-orange-300 border-orange-500/30 bg-orange-500/10",
  Critical: "text-red-300 border-red-500/30 bg-red-500/10",
};

const localNow = () => orgNowLocal().format("YYYY-MM-DDTHH:mm");

// participant: an id, "" for None / staff only, or null when not chosen yet.
const blankForm = (participant) => ({
  types: [], participant, incident_datetime: localNow(), location: "",
  severity: "Medium", leading_up: "", description: "",
  immediate_action: "", witnesses: "", behaviour_details: "", injury_party: "",
  self_harm_details: "", medication_details: "", vehicle_rego: "", vehicle_details: "",
  police_attended: null, police_report_number: "", other_details: "",
});
const blankInjury = () => ({
  person_type: "Participant", person_name: "", body_location: "", body_map: [], nature_of_injury: "",
  first_aid_given: "", medical_treatment_sought: false, hospital: false,
});
// One participant is picked for you; with several, the worker must choose (ISSUE-00326:
// preselecting the first one sent reports in under the wrong person or none).
const defaultParticipant = (participants) => (participants.length === 1 ? participants[0].participant : participants.length ? null : "");
const parseMap = (v) => { try { const a = JSON.parse(v || "[]"); return Array.isArray(a) ? a : []; } catch { return []; } };

const lbl = "text-[11px] font-semibold text-white/80 mb-1 block";
const inputCls = "bg-black/20 border-white/10 text-white placeholder:text-slate-500 text-sm";
const telHref = (n) => "tel:" + (n || "").replace(/[^0-9+]/g, "");

// Business hours: Mon–Fri 09:00–17:00 org time.
const inBusinessHours = () => { const d = orgNowLocal(); const day = d.day(); const h = d.hour(); return day >= 1 && day <= 5 && h >= 9 && h < 17; };

function EscalationAlert({ office = {} }) {
  const business = inBusinessHours();
  const Line = ({ label, number, pin, active }) => (
    <a href={telHref(number)} data-testid={`incident-call-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 mt-2 transition-colors ${active ? "border-red-400 bg-red-500/20" : "border-white/15 bg-black/20"}`}>
      <div>
        <div className="text-[10px] uppercase tracking-wide text-red-200/80">{label}{active ? " · call now" : ""}</div>
        <div className="text-white font-bold text-base flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {number || "—"}</div>
        {pin ? <div className="text-[10px] text-red-200/80 mt-0.5">Access PIN: <span className="font-semibold text-white">{pin}</span></div> : null}
      </div>
    </a>
  );
  return (
    <div className="rounded-xl border border-red-500/60 bg-red-500/15 p-3" data-testid="incident-escalation-alert">
      <div className="flex items-center gap-1.5 text-[12px] font-bold text-red-200">
        <AlertTriangle className="h-4 w-4" /> Call the office immediately
      </div>
      <p className="text-[11px] text-red-100/90 mt-1">This is a High/Critical incident. Phone the office now before finishing this report.</p>
      <Line label="During office hours" number={office.business} active={business} />
      <Line label="After hours" number={office.afterhours} pin={office.pin} active={!business} />
    </div>
  );
}

function ExistingPhoto({ shiftName, iname, idx, name }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let obj;
    api.fetchIncidentPhotoBlob(shiftName, iname, idx).then((r) => { obj = r.url; setUrl(r.url); }).catch(() => {});
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [shiftName, iname, idx]);
  if (!url) return <div className="h-16 w-16 rounded-md bg-black/30 border border-white/10 flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-500" /></div>;
  return <img src={url} alt={name || "photo"} data-testid={`incident-existing-photo-${idx}`} className="h-16 w-16 rounded-md object-cover border border-white/10" />;
}

export function IncidentForm({ shiftName, participants = [], incidents = [], onSaved, locked = false, office = {} }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState(() => blankForm(defaultParticipant(participants)));
  const [injuries, setInjuries] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [photos, setPhotos] = useState([]);          // new photos to upload: {file, url}
  const [existingPhotos, setExistingPhotos] = useState([]); // [{idx, file_name}]

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const { enqueue } = useOffline() || {};
  const toggleType = (t) =>
    setF((s) => ({ ...s, types: s.types.includes(t) ? s.types.filter((x) => x !== t) : [...s.types, t] }));
  const has = (t) => f.types.includes(t);
  const escalate = f.severity === "High" || f.severity === "Critical";

  const setInj = (i, k, v) => setInjuries((a) => a.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const reset = () => {
    photos.forEach((p) => URL.revokeObjectURL(p.url));
    setF(blankForm(defaultParticipant(participants))); setInjuries([]); setPhotos([]);
    setExistingPhotos([]); setOpen(false); setEditingId(null);
  };

  const addPhotos = (fileList) => {
    const files = Array.from(fileList || []).filter((x) => x.type.startsWith("image/"));
    setPhotos((p) => [...p, ...files.map((file) => ({ file, url: URL.createObjectURL(file) }))]);
  };
  const removePhoto = (i) => setPhotos((p) => { URL.revokeObjectURL(p[i].url); return p.filter((_, idx) => idx !== i); });

  // One photo per upload, so a big batch never hits the server's request limit.
  const uploadPhotos = async (iname) => {
    const failed = [];
    for (const p of photos) {
      const fd = new FormData();
      fd.append("photos", p.file);
      try { await api.workerAddIncidentPhotos(shiftName, iname, fd); }
      catch (e) { failed.push(e.response?.data?.detail || "no connection"); }
    }
    if (failed.length) {
      throw new Error(`${failed.length} photo${failed.length === 1 ? "" : "s"} didn't upload (${[...new Set(failed)].join(" ")})`);
    }
  };

  const startEdit = async (inc) => {
    try {
      const full = await api.workerGetIncident(shiftName, inc.name);
      setF({
        types: full.types || [], participant: full.participant || (participants.length > 1 ? null : ""),
        incident_datetime: (full.incident_datetime || "").slice(0, 16).replace(" ", "T"),
        location: full.location || "", severity: full.severity || "Medium",
        leading_up: full.leading_up || "", description: full.description || "",
        immediate_action: full.immediate_action || "", witnesses: full.witnesses || "",
        behaviour_details: full.behaviour_details || "", injury_party: full.injury_party || "",
        self_harm_details: full.self_harm_details || "", medication_details: full.medication_details || "",
        vehicle_rego: full.vehicle_rego || "", vehicle_details: full.vehicle_details || "",
        police_attended: full.police_attended ? true : (full.vehicle_rego || full.vehicle_details ? false : null),
        police_report_number: full.police_report_number || "", other_details: full.other_details || "",
      });
      setInjuries((full.injuries || []).map((i) => ({
        person_type: i.person_type || "Participant", person_name: i.person_name || "",
        body_location: i.body_location || "", body_map: parseMap(i.body_map), nature_of_injury: i.nature_of_injury || "",
        first_aid_given: i.first_aid_given || "",
        medical_treatment_sought: !!i.medical_treatment_sought, hospital: !!i.hospital,
      })));
      setExistingPhotos(full.photos || []);
      setPhotos([]);
      setEditingId(inc.name);
      setOpen(true);
    } catch {
      toast.error("Could not open that incident for editing");
    }
  };

  const submit = async () => {
    if (f.types.length === 0) return toast.error("Select at least one incident type");
    if (!f.leading_up.trim()) return toast.error("Describe what happened leading up to the event");
    if (!f.description.trim()) return toast.error("Describe what happened");
    if (!f.immediate_action.trim()) return toast.error("Record the immediate action taken");
    if (!f.incident_datetime) return toast.error("Set the incident date/time");
    if (has("vehicle") && f.police_attended === null) return toast.error("Did the police attend the vehicle incident?");
    if (f.participant === null) return toast.error("Choose the participant involved, or None / staff only");
    const payload = {
      ...f,
      participant: f.participant || null,
      police_attended: has("vehicle") ? !!f.police_attended : false,
      police_report_number: has("vehicle") && f.police_attended ? f.police_report_number.trim() : "",
      incident_datetime: f.incident_datetime.replace("T", " ") + ":00",
      injuries: has("injury") ? injuries.map((i) => ({ ...i, body_map: JSON.stringify(i.body_map || []) })) : [],
    };
    setBusy(true);
    if (editingId) {
      try {
        await api.workerEditIncident(shiftName, editingId, payload);
        try { await uploadPhotos(editingId); } catch (e) { toast.error(`Incident saved, but ${e.message}. Try again with a connection.`); }
        toast.success("Incident updated and resubmitted to the office");
        reset();
        onSaved?.();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not resubmit incident");
      } finally {
        setBusy(false);
      }
      return;
    }
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      if (photos.length) toast.message("You're offline — photos will need to be added later with a connection");
      await enqueue?.("incident", shiftName, payload);
      toast.success("Incident saved offline — will sync when you're back online");
      reset();
      setBusy(false);
      return;
    }
    try {
      const res = await api.workerAddIncident(shiftName, payload);
      try { await uploadPhotos(res.name); } catch (e) { toast.error(`Incident saved, but ${e.message}. Try again with a connection.`); }
      toast.success("Incident report submitted to the office");
      reset();
      onSaved?.();
    } catch (e) {
      if (isOfflineError(e)) {
        await enqueue?.("incident", shiftName, payload);
        toast.success("Incident saved offline — will sync when you're back online");
        reset();
        return;
      }
      toast.error(e.response?.data?.detail || "Could not submit incident");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-2xl border border-red-500/20 bg-red-500/5 p-4" data-testid="worker-incident-section">
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-red-300/90 mb-3">
        <ShieldAlert className="h-3.5 w-3.5" /> Incident Reporting
      </div>

      {incidents.length > 0 && (
        <div className="space-y-2 mb-3">
          {incidents.map((inc) => (
            <div key={inc.name} className="rounded-xl bg-black/20 border border-white/10 p-3" data-testid={`worker-incident-${inc.name}`}>
              <div className="flex items-center justify-between">
                <div className="text-white text-sm font-semibold">{inc.name}</div>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${SEV_STYLE[inc.severity] || SEV_STYLE.Medium}`}>{inc.severity}</span>
              </div>
              <div className="text-[11px] text-slate-300 mt-1">{(inc.types || []).join(", ")}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">{inc.incident_datetime} · {inc.status}{inc.photo_count ? ` · ${inc.photo_count} photo${inc.photo_count === 1 ? "" : "s"}` : ""}</div>
              {inc.status === "Returned" && (
                <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2" data-testid={`worker-incident-returned-${inc.name}`}>
                  <div className="text-[11px] font-semibold text-amber-300">Returned by the office — please update &amp; resubmit</div>
                  {inc.return_reason && <div className="text-[11px] text-amber-200/80 mt-0.5">"{inc.return_reason}"</div>}
                  <Button onClick={() => startEdit(inc)} data-testid={`worker-incident-edit-${inc.name}`}
                    className="mt-2 h-8 text-xs font-semibold bg-amber-600 hover:bg-amber-700">Edit &amp; resubmit</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {locked && !open ? (
        <div className="text-[11px] text-slate-400 text-center py-2" data-testid="worker-incident-locked">
          Incident reporting is locked after the timesheet is submitted. Ask the office to return it if you need changes.
        </div>
      ) : !open ? (
        <Button onClick={() => setOpen(true)} data-testid="worker-incident-open"
          className="w-full h-10 font-semibold bg-red-600 hover:bg-red-700">
          <Plus className="h-4 w-4 mr-1.5" /> Report an Incident
        </Button>
      ) : (
        <div className="space-y-4 rounded-xl border border-white/10 bg-black/20 p-3" data-testid="worker-incident-form">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-white">{editingId ? `Edit incident ${editingId}` : "New incident report"}</div>
            <button onClick={reset} data-testid="worker-incident-cancel" className="text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
          </div>

          {/* Types */}
          <div>
            <span className={lbl}>Incident type(s) <span className="text-red-400">*</span></span>
            <div className="flex flex-wrap gap-2">
              {TYPES.map(([k, label]) => (
                <button key={k} type="button" onClick={() => toggleType(k)} data-testid={`worker-incident-type-${k}`}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors ${has(k) ? "bg-red-500 text-white border-red-500" : "bg-transparent text-slate-300 border-white/15"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* When / where */}
          {/* Stacked on phones: iOS gives a date-time box a fixed minimum width, so side by side it ran into Location. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="min-w-0">
              <span className={lbl}>Date & time <span className="text-red-400">*</span></span>
              <Input type="datetime-local" value={f.incident_datetime} onChange={(e) => set("incident_datetime", e.target.value)}
                data-testid="worker-incident-datetime" className={`${inputCls} min-w-0 w-full appearance-none`} />
            </div>
            <div className="min-w-0">
              <span className={lbl}>Location</span>
              <Input value={f.location} onChange={(e) => set("location", e.target.value)}
                data-testid="worker-incident-location" placeholder="Where did it happen?" className={inputCls} />
            </div>
          </div>

          {/* Participant */}
          {participants.length > 0 && (
            <div>
              <span className={lbl}>Participant involved{participants.length > 1 && <span className="text-red-400"> *</span>}</span>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => set("participant", "")} data-testid="worker-incident-participant-none"
                  className={`text-xs font-semibold px-3 py-1.5 rounded-md border ${f.participant === "" ? "bg-[#00c86e] text-white border-[#00c86e]" : "bg-transparent text-slate-300 border-white/15"}`}>None / staff only</button>
                {participants.map((p) => (
                  <button key={p.participant} type="button" onClick={() => set("participant", p.participant)}
                    data-testid={`worker-incident-participant-${p.participant}`}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-md border ${f.participant === p.participant ? "bg-[#00c86e] text-white border-[#00c86e]" : "bg-transparent text-slate-300 border-white/15"}`}>
                    {p.participant_name || p.participant}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Severity */}
          <div>
            <span className={lbl}>Severity</span>
            <div className="flex gap-2">
              {SEVERITIES.map((sv) => (
                <button key={sv} type="button" onClick={() => set("severity", sv)} data-testid={`worker-incident-sev-${sv}`}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-md border ${f.severity === sv ? SEV_STYLE[sv] + " !border-current" : "bg-transparent text-slate-300 border-white/15"}`}>{sv}</button>
              ))}
            </div>
          </div>

          {escalate && <EscalationAlert office={office} />}

          {/* Narrative */}
          <div>
            <span className={lbl}>What happened leading up to the event <span className="text-red-400">*</span></span>
            <Textarea rows={2} value={f.leading_up} onChange={(e) => set("leading_up", e.target.value)}
              data-testid="worker-incident-leading" placeholder="Context / triggers before the incident…" className={inputCls} />
          </div>
          <div>
            <span className={lbl}>Description of what happened <span className="text-red-400">*</span></span>
            <Textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)}
              data-testid="worker-incident-description" placeholder="Describe the incident in detail…" className={inputCls} />
          </div>
          <div>
            <span className={lbl}>Immediate action taken <span className="text-red-400">*</span></span>
            <Textarea rows={2} value={f.immediate_action} onChange={(e) => set("immediate_action", e.target.value)}
              data-testid="worker-incident-action" placeholder="What did you do straight away?" className={inputCls} />
          </div>
          <div>
            <span className={lbl}>Witnesses</span>
            <Input value={f.witnesses} onChange={(e) => set("witnesses", e.target.value)}
              data-testid="worker-incident-witnesses" placeholder="Names of anyone who saw it" className={inputCls} />
          </div>

          {/* Type-specific sections */}
          {has("behaviour") && (
            <div data-testid="worker-incident-sec-behaviour">
              <span className={lbl}>Behaviour details</span>
              <Textarea rows={2} value={f.behaviour_details} onChange={(e) => set("behaviour_details", e.target.value)} className={inputCls} data-testid="worker-incident-behaviour" />
            </div>
          )}

          {has("injury") && (
            <div className="rounded-lg border border-white/10 p-3 space-y-3" data-testid="worker-incident-sec-injury">
              <div>
                <span className={lbl}>Who was injured</span>
                <div className="flex gap-2">
                  {["Staff", "Participant", "Both"].map((p) => (
                    <button key={p} type="button" onClick={() => set("injury_party", p)} data-testid={`worker-incident-injparty-${p}`}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-md border ${f.injury_party === p ? "bg-red-500 text-white border-red-500" : "bg-transparent text-slate-300 border-white/15"}`}>{p}</button>
                  ))}
                </div>
              </div>
              {injuries.map((inj, i) => (
                <div key={i} className="rounded-lg bg-black/30 border border-white/10 p-3 space-y-2" data-testid={`worker-injury-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                      {["Staff", "Participant"].map((pt) => (
                        <button key={pt} type="button" onClick={() => setInj(i, "person_type", pt)} data-testid={`worker-injury-${i}-type-${pt}`}
                          className={`text-[11px] font-semibold px-2.5 py-1 rounded border ${inj.person_type === pt ? "bg-[#00c86e] text-white border-[#00c86e]" : "bg-transparent text-slate-300 border-white/15"}`}>{pt}</button>
                      ))}
                    </div>
                    <button onClick={() => setInjuries((a) => a.filter((_, idx) => idx !== i))} data-testid={`worker-injury-${i}-remove`} className="text-slate-400 hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                  <Input value={inj.person_name} onChange={(e) => setInj(i, "person_name", e.target.value)} placeholder="Name" className={inputCls} data-testid={`worker-injury-${i}-name`} />
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={inj.body_location} onChange={(e) => setInj(i, "body_location", e.target.value)} placeholder="Body location (notes)" className={inputCls} data-testid={`worker-injury-${i}-body`} />
                    <Input value={inj.nature_of_injury} onChange={(e) => setInj(i, "nature_of_injury", e.target.value)} placeholder="Nature of injury" className={inputCls} data-testid={`worker-injury-${i}-nature`} />
                  </div>
                  <div className="rounded-lg border border-white/10 bg-black/20 p-2.5">
                    <div className={lbl}>Mark injury on body map</div>
                    <BodyMap testid={`worker-injury-${i}-bodymap`} value={inj.body_map} onChange={(v) => setInj(i, "body_map", v)} />
                  </div>
                  <Input value={inj.first_aid_given} onChange={(e) => setInj(i, "first_aid_given", e.target.value)} placeholder="First aid given" className={inputCls} data-testid={`worker-injury-${i}-firstaid`} />
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                      <Checkbox checked={inj.medical_treatment_sought} onCheckedChange={(v) => setInj(i, "medical_treatment_sought", !!v)} className="border-white/30" data-testid={`worker-injury-${i}-medical`} /> Medical treatment sought
                    </label>
                    <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                      <Checkbox checked={inj.hospital} onCheckedChange={(v) => setInj(i, "hospital", !!v)} className="border-white/30" data-testid={`worker-injury-${i}-hospital`} /> Hospital / ambulance
                    </label>
                  </div>
                </div>
              ))}
              <Button onClick={() => setInjuries((a) => [...a, blankInjury()])} variant="outline" data-testid="worker-injury-add"
                className="h-8 text-xs font-semibold bg-transparent border-white/15 text-white hover:bg-white/10">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add injured person
              </Button>
            </div>
          )}

          {has("self_harm") && (
            <div data-testid="worker-incident-sec-self_harm">
              <span className={lbl}>Self harm details</span>
              <Textarea rows={2} value={f.self_harm_details} onChange={(e) => set("self_harm_details", e.target.value)} className={inputCls} data-testid="worker-incident-selfharm" />
            </div>
          )}
          {has("medication") && (
            <div data-testid="worker-incident-sec-medication">
              <span className={lbl}>Medication error / issue details</span>
              <Textarea rows={2} value={f.medication_details} onChange={(e) => set("medication_details", e.target.value)} className={inputCls} data-testid="worker-incident-medication" />
            </div>
          )}
          {has("vehicle") && (
            <div className="space-y-2 rounded-lg border border-white/10 p-3" data-testid="worker-incident-sec-vehicle">
              <div>
                <span className={lbl}>Vehicle registration</span>
                <Input value={f.vehicle_rego} onChange={(e) => set("vehicle_rego", e.target.value)} placeholder="Rego" className={inputCls} data-testid="worker-incident-vehicle-rego" />
              </div>
              <div>
                <span className={lbl}>Vehicle incident details</span>
                <Textarea rows={2} value={f.vehicle_details} onChange={(e) => set("vehicle_details", e.target.value)} className={inputCls} data-testid="worker-incident-vehicle-details" />
              </div>
              <div>
                <span className={lbl}>Did the police attend? <span className="text-red-400">*</span></span>
                <div className="flex gap-2">
                  {[["Yes", true], ["No", false]].map(([t, v]) => (
                    <button key={t} type="button" onClick={() => set("police_attended", v)} data-testid={`worker-incident-police-${v ? "yes" : "no"}`}
                      className={`text-xs font-semibold px-4 py-1.5 rounded-md border transition-colors ${f.police_attended === v ? "bg-[#bf3b4f] text-white border-[#bf3b4f]" : "bg-transparent text-slate-300 border-white/15"}`}>{t}</button>
                  ))}
                </div>
              </div>
              {f.police_attended === true && (
                <div>
                  <span className={lbl}>Police report number <span className="text-slate-500 font-normal">(optional)</span></span>
                  <Input value={f.police_report_number} onChange={(e) => set("police_report_number", e.target.value)} placeholder="e.g. QP2400123" className={inputCls} data-testid="worker-incident-police-report" />
                </div>
              )}
            </div>
          )}
          {has("other") && (
            <div data-testid="worker-incident-sec-other">
              <span className={lbl}>Other details</span>
              <Textarea rows={2} value={f.other_details} onChange={(e) => set("other_details", e.target.value)} className={inputCls} data-testid="worker-incident-other" />
            </div>
          )}

          {/* Photos */}
          <div className="rounded-lg border border-white/10 p-3" data-testid="worker-incident-photos">
            <span className={lbl}>Photos <span className="text-slate-500 font-normal">(optional · attach when online)</span></span>
            {existingPhotos.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2" data-testid="worker-incident-existing-photos">
                {existingPhotos.map((p) => <ExistingPhoto key={p.idx} shiftName={shiftName} iname={editingId} idx={p.idx} name={p.file_name} />)}
              </div>
            )}
            {photos.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {photos.map((p, i) => (
                  <div key={i} className="relative" data-testid={`worker-incident-photo-${i}`}>
                    <img src={p.url} alt="attachment" className="h-16 w-16 rounded-md object-cover border border-white/10" />
                    <button type="button" onClick={() => removePhoto(i)} data-testid={`worker-incident-photo-remove-${i}`}
                      className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-600 text-white flex items-center justify-center border border-white"><X className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
            )}
            <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer w-fit" data-testid="worker-incident-photo-label">
              <span className="flex items-center gap-1.5 rounded-md border border-white/15 px-3 py-2 hover:bg-white/10">
                <ImagePlus className="h-3.5 w-3.5" /> Add photos
              </span>
              <input type="file" accept="image/*" multiple className="hidden" data-testid="worker-incident-photo-input"
                onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} />
            </label>
          </div>

          <Button onClick={submit} disabled={busy} data-testid="worker-incident-submit"
            className="w-full h-10 font-semibold bg-red-600 hover:bg-red-700">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Incident Report"}
          </Button>
          <p className="text-[10px] text-slate-500 text-center">Once submitted, only the office can manage this incident.</p>
        </div>
      )}
    </div>
  );
}
